import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import ErrorMessage from "../components/common/ErrorMessage";
import { useEnrollment } from "../context/EnrollmentContext";
import LockedSection from "../components/LockedSection";

const TestSeriesPage = () => {
  // State for data
  const [testSeriesPrograms, setTestSeriesPrograms] = useState([]);
  const [subjectTests, setSubjectTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const { activeEnrollments, loading: enrollmentLoading } = useEnrollment();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  // Function to fetch data from Firestore
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch test series programs
      const programsSnapshot = await getDocs(
        collection(db, "testSeriesPrograms")
      );
      const programsData = programsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch subject tests
      const subjectsSnapshot = await getDocs(collection(db, "subjectTests"));
      const subjectsData = subjectsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setTestSeriesPrograms(programsData);
      setSubjectTests(subjectsData);

      // Set the first program as active if programs exist
      if (programsData.length > 0) {
        setActiveTab(programsData[0].id);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Please refresh the page.");
      setLoading(false);
    }
  };

  // Default programs if Firestore data is not loaded yet
  const defaultPrograms = [
    {
      id: "english",
      title: "10th Standard English Medium",
      tests: "12 Tests",
      description:
        "Comprehensive test series covering the entire English Medium Syllabus for 10th Standard",
      features: [
        "Full syllabus coverage",
        "Chapter-wise tests",
        "Model question papers",
        "Performance analysis",
        "Detailed solutions",
        "Doubt clearing sessions",
      ],
      price: "₹3,500",
      image:
        "https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: true,
    },
    {
      id: "kannada",
      title: "10th Standard Kannada Medium",
      tests: "12 Tests",
      description:
        "Comprehensive test series in Kannada Medium for 10th Standard students",
      features: [
        "Full syllabus coverage in Kannada",
        "Chapter-wise tests",
        "Model question papers",
        "Performance analysis",
        "Detailed solutions",
        "Doubt clearing sessions",
      ],
      price: "₹3,500",
      image:
        "https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: false,
    },
    {
      id: "ncert",
      title: "10th Standard NCERT (Maths + Science)",
      tests: "15 Tests",
      description:
        "Focused test series for NCERT Maths and Science for 10th Standard students",
      features: [
        "Comprehensive NCERT coverage",
        "Chapter-wise tests",
        "Topic-wise analysis",
        "Performance tracking",
        "Detailed solutions",
        "Doubt clearing sessions",
      ],
      price: "₹4,000",
      image:
        "https://images.unsplash.com/photo-1576334761529-e8d00379d4ce?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: false,
    },
    {
      id: "navodaya",
      title: "Navodaya Entrance Test Series",
      tests: "10 Tests",
      description:
        "Specialized test series for Jawahar Navodaya Vidyalaya entrance preparation",
      features: [
        "Previous year questions",
        "Mock tests",
        "Section-wise practice",
        "Performance analysis",
        "Detailed solutions",
        "Expert guidance",
      ],
      price: "₹2,500",
      image:
        "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: true,
    },
    {
      id: "morarji",
      title: "Morarji Entrance Test Series",
      tests: "10 Tests",
      description:
        "Specialized test series for Morarji Desai Residential School entrance preparation",
      features: [
        "Previous year questions",
        "Mock tests",
        "Section-wise practice",
        "Performance analysis",
        "Detailed solutions",
        "Expert guidance",
      ],
      price: "₹2,500",
      image:
        "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: false,
    },
  ];

  // Default subject tests if Firestore data is not loaded yet
  const defaultSubjectTests = [
    {
      id: "maths",
      title: "Mathematics",
      chapters: [
        { id: "chapter1", title: "Chapter 1", tests: 5 },
        { id: "chapter2", title: "Chapter 2", tests: 4 },
        { id: "chapter3", title: "Chapter 3", tests: 3 },
      ],
    },
    {
      id: "science",
      title: "Science",
      chapters: [
        { id: "chapter1", title: "Chapter 1", tests: 5 },
        { id: "chapter2", title: "Chapter 2", tests: 4 },
        { id: "chapter3", title: "Chapter 3", tests: 3 },
      ],
    },
    {
      id: "social",
      title: "Social Science",
      chapters: [
        { id: "chapter1", title: "Chapter 1", tests: 5 },
        { id: "chapter2", title: "Chapter 2", tests: 4 },
        { id: "chapter3", title: "Chapter 3", tests: 3 },
      ],
    },
  ];

  const benefits = [
    {
      id: 1,
      title: "Exam Simulation",
      description:
        "Experience the actual exam environment with timed tests that follow the exact pattern.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: 2,
      title: "Detailed Analysis",
      description:
        "Get comprehensive performance analytics to identify your strengths and areas needing improvement.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: 3,
      title: "Expert Evaluation",
      description:
        "Have your answers evaluated by experienced faculty members who understand examination requirements.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
    },
  ];

  const testimonials = [
    {
      id: 1,
      content:
        "The test series helped me score 95% in my exams. The detailed explanations after each test helped me understand concepts better and improved my accuracy.",
      author: "Ravi Kumar",
      rank: "10th Std Topper 2023",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT5HVVqbKEfRB2KnjIkT9XmJfWcENcZObNPuQ&s",
    },
    {
      id: 2,
      content:
        "What sets this test series apart is the quality of questions and the comprehensive performance analysis. It helped me identify my weak areas and focus my preparation accordingly.",
      author: "Meera Singh",
      rank: "Navodaya Selected 2023",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSDjtNi1m_f5OuxG9GcNMqa9i4F2sq_dVREdA&s",
    },
    {
      id: 3,
      content:
        "The chapter-wise tests and model papers were extremely helpful. The feedback from faculty members helped me improve my answering strategy.",
      author: "Anuj Patel",
      rank: "Morarji School Selected 2023",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTyBOAHtlqzaow5_rlz81tF3SwDxw4m5bYPzQ&s",
    },
  ];

  const faqs = [
    {
      question: "How can I access the test series?",
      answer:
        "Our test series is available both online and offline. You can take the tests at our centers or access them online through our web portal or mobile app.",
    },
    {
      question: "How soon will I get my evaluated answer sheets?",
      answer:
        "For objective tests, results are available immediately after submission. For subjective tests, evaluated answer sheets are provided within 3-5 days with detailed feedback.",
    },
    {
      question: "Is there a validity period for the test series?",
      answer:
        "Yes, the test series is valid for one academic year from the date of purchase.",
    },
    {
      question: "Can I get a demo test before purchasing?",
      answer:
        "Yes, we offer a free demo test to help you understand the quality and pattern of our test series. You can access it from our website after registration.",
    },
    {
      question: "Do you provide additional study material?",
      answer:
        "Yes, we provide comprehensive study notes and resources to help you prepare for the tests effectively.",
    },
    {
      question: "Can I access previous tests after completing them?",
      answer:
        "Yes, you can review all your previous tests, answers, and evaluations throughout the validity period of your subscription.",
    },
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-blue-600 font-medium">Loading test series...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return <ErrorMessage message={error} />;
  }

  // Use Firestore data if available, otherwise use default data
  const displayPrograms =
    testSeriesPrograms.length > 0 ? testSeriesPrograms : defaultPrograms;
  const displaySubjects =
    subjectTests.length > 0 ? subjectTests : defaultSubjectTests;
  if (activeEnrollments?.length === 0) {
    return <LockedSection />;
  }
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gray-900 opacity-90"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80')] opacity-10"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <motion.h1
              className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Test Series for Excellence
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl mb-8 text-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Comprehensive test series designed by experts to help you assess,
              analyze, and improve your preparation for examinations.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link
                to="/classroom"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-900 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-300 shadow-md"
              >
                Enroll Now
              </Link>
              <Link
                to="/take-test/IVkK01sgQaFJVtMN8sXN"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
              >
                Try Free Sample Test
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Subject-wise Tests Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Subject-wise Test Series
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Focused tests to strengthen your understanding of individual
              subjects and chapters
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {displaySubjects.map((subject, index) => (
              <motion.div
                key={subject.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-blue-800 mb-4">
                  {subject.title}
                </h3>
                <ul className="space-y-3">
                  {subject.chapters.map((chapter) => (
                    <li
                      key={chapter.id}
                      className="border-b border-gray-100 pb-2"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">{chapter.title}</span>
                        <span className="text-sm bg-blue-100 text-blue-800 py-1 px-2 rounded-full">
                          {chapter.tests} Tests
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link
                    to={`/subject-tests/${subject.id}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    View all {subject.title} tests
                    <svg
                      className="ml-2 w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Why Choose Our Test Series?
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Our test series is designed to give you the competitive edge in
              your preparations
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 text-center"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mb-4 mx-auto">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Success Stories
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Hear from our students who achieved success with our test series
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg border border-gray-200 shadow-md"
              >
                <p className="italic text-gray-600 mb-6">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <div className="mr-4 h-12 w-12 rounded-full overflow-hidden border-2 border-blue-500">
                    <img
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">
                      {testimonial.author}
                    </h3>
                    <p className="text-blue-600 font-medium">
                      {testimonial.rank}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-gray-900 sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Find answers to common questions about our test series
            </motion.p>
          </div>

          <div className="max-w-3xl mx-auto divide-y divide-gray-200">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="py-6"
              >
                <h3 className="text-lg font-medium text-gray-900">
                  {faq.question}
                </h3>
                <p className="mt-2 text-gray-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              className="text-3xl font-extrabold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Ready to Test Your Preparation?
            </motion.h2>
            <motion.p
              className="text-lg text-gray-200 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Join thousands of successful students who have improved their test
              scores with our comprehensive test series.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link
                to="/classroom"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-900 bg-yellow-400 hover:bg-yellow-500 transition-colors duration-300 shadow-md"
              >
                Enroll Now
              </Link>
              
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestSeriesPage;
